import { call, put, select, takeLatest, takeEvery } from 'redux-saga/effects'
import { PayloadAction } from '@reduxjs/toolkit'
import { toast } from 'react-toastify'
import TripApi from './TripApi'
import {
  setLoading,
  setError,
  setTrips,
  setTripTickets,
  setTripTicketsLoading,
  selectTripFilters,
  setDelayReRoute,
  setClusters,
  requestExportTripCSVSuccess,
  requestExportTripCSVFail,
  setUnassignedTickets,
  setManualRouting,
  setManualRouteSuccessCount,
  setManualRoutingStatus,
  setManualRouteCooldownUntil,
} from './TripSlice'
import { TripCmd, GetTripTicketsCmd, GetTripTicketQry, TripClusterModel, GetTripResponse, CheckEnabledManualRoutingQry, ManualRoutingCooldownQry } from '../models/Trip'
import { ToastDefaultConfig } from '../../../../setup/toast/ToastConfig'
import { Response } from '../../../../setup/axios/HttpResponse'
import { HTTP_RESPONSE_STATUS } from '../../../../setup/constants/Http'
import moment from 'moment'
import i18n from 'i18next'
import { TripExportResult } from './TripApi'
import { clientTimeZoneOffset } from 'app/utils/common/constants'

// Action types
const FETCH_TRIPS = 'trip/fetchTrips'
const FETCH_TRIP_TICKETS = 'trip/fetchTripTickets'
const REROUTE = 'trip/rerouteTrips'
const FETCH_CLUSTERS = 'trip/fetchClustersSaga'
const EXPORT_TRIP_CSV = 'trip/exportTripCSV'
const FETCH_UNASSIGNED_TICKETS = 'trip/fetchUnassignedTickets'
const MANUAL_ROUTE_TRIP = 'trip/manualRouteTrip'
const CHECK_ENABLED_MANUAL_ROUTING = 'trip/checkEnabledManualRouting'
const GET_MANUAL_ROUTING_COOLDOWN = 'trip/getManualRoutingCooldown'

// Action creators
export const fetchTrips = (silent = false) => ({ type: FETCH_TRIPS, payload: { silent } })
export const fetchTripTickets = (tripId: string, silent = false) => ({
  type: FETCH_TRIP_TICKETS,
  payload: { tripId, silent }
})
export const rerouteTrips = (tripId: string) => ({
  type: REROUTE,
  payload: tripId
})

export const fetchClusters = () => ({ 
  type: FETCH_CLUSTERS, 
})

export const requestExportTripCSV = () => ({
  type: EXPORT_TRIP_CSV,
})

export const fetchUnassignedTickets = () => ({
  type: FETCH_UNASSIGNED_TICKETS,
})

export const sendToRoutingEngine = () => ({
  type: MANUAL_ROUTE_TRIP,
})

export const fetchManualRoutingStatus = () => ({
  type: CHECK_ENABLED_MANUAL_ROUTING,
})

export const fetchManualRoutingCooldown = () => ({
  type: GET_MANUAL_ROUTING_COOLDOWN,
})

// Worker Sagas
function* fetchTripsSaga(
  action?: PayloadAction<{ silent?: boolean }>
): Generator<any, void, any> {
  // Silent refetch keeps the table visible (no full-page loading spinner),
  // e.g. after a re-route where we only need lastRefreshedAt to update.
  const silent = action?.payload?.silent === true
  try {
    if (!silent) yield put(setLoading(true))
    yield put(setError(null))

    // Get current filters from state
    const filters = yield select(selectTripFilters)

    // Build API command - Search and Status must be strings (not undefined)
    const cmd: TripCmd = {
      serviceDate: filters.serviceDate?.toISOString() || new Date().toISOString(),
      status: filters.status || '', // Empty string instead of undefined
      clusterId: filters.clusterId || undefined,
      search: filters.search || '', // Empty string instead of undefined
      clientTimeZone: new Date().getTimezoneOffset() * -1, // Convert to positive offset
      language: filters.language || 'en',
      cityId: filters.cityId || undefined,
      housingId: filters.housingId || undefined,
    }

    // Call API
    const tripResponse: GetTripResponse = yield call(TripApi.getTrips, cmd)

    yield put(setTrips({
      trips: tripResponse.lstTrips,
      totalTrips: tripResponse.totalTrips,
    }))
  } catch (error: any) {
    console.error('Trip fetch error:', error)
    const errorMessage = error?.response?.data?.message || 'Failed to fetch trips'
    yield put(setError(errorMessage))
    toast.error(errorMessage, ToastDefaultConfig())
  } finally {
    if (!silent) yield put(setLoading(false))
  }
}

function* fetchTripTicketsSaga(
  action: PayloadAction<{ tripId: string; silent?: boolean }>
): Generator<any, void, any> {
  const { tripId, silent } = action.payload
  try {
    // Silent refetch keeps the expanded ticket table visible (no spinner),
    // e.g. after a re-route where the sequence/routes may have changed.
    if (!silent) yield put(setTripTicketsLoading({ tripId, loading: true }))

    const filters = yield select(selectTripFilters)

    const cmd: GetTripTicketsCmd = {
      tripId,
      search: filters.search || '',
      language: filters.language || 'en',
      clientTimeZone: new Date().getTimezoneOffset() * -1, // Convert to positive offset,
    }

    const tickets: GetTripTicketQry[] = yield call(TripApi.getTripTickets, cmd)

    // Update state
    yield put(setTripTickets({ tripId, tickets }))

    // If the trip has no tickets left (e.g. they were all rescheduled away),
    // the trips list itself is stale — refresh it so counts/rows reflect the
    // new state. Keep it silent so the table doesn't flash a spinner.
    if (!tickets || tickets.length === 0) {
      yield put(fetchTrips(true))
    }
  } catch (error: any) {
    console.error('fetchTripTicketsSaga - error:', error)
    const errorMessage = error?.response?.data?.message || 'Failed to fetch trip tickets'
    toast.error(errorMessage, ToastDefaultConfig())
  } finally {
    if (!silent) yield put(setTripTicketsLoading({ tripId, loading: false }))
  }
}

function* reRouteStrip(action: PayloadAction<string>): Generator<any, void, any> {
  try {
    // Disable the button immediately while the API call is in flight.
    yield put(setDelayReRoute(true))

    // Call API
    const response = yield call(TripApi.reRoute, action.payload)

    if (response) {
      // Silently refetch trips so lastRefreshedAt is updated from server
      // (drives the Re-Route cooldown) without hiding the table behind a
      // full-page loading spinner. Wait for it to finish so the per-trip
      // cooldown is in place before we drop the in-flight flag below.
      yield call(fetchTripsSaga, fetchTrips(true))

      // Silently refresh the expanded ticket table for this trip, since the
      // re-route may have changed the ticket sequence/routes.
      yield call(fetchTripTicketsSaga, fetchTripTickets(action.payload, true))

      toast.success(i18n.t('TRIP_PAGE.RE_ROUTE_SUCCESS'), ToastDefaultConfig())
    }
  } catch (error: any) {
    console.error('reRouteStrip - error:', error)
    const errorMessage = error?.response?.data?.message || 'Failed to re-route trip'
    toast.error(errorMessage, ToastDefaultConfig())
  } finally {
    // Stop the spinner / release the in-flight lock. On success the
    // per-trip lastRefreshedAt cooldown keeps this button disabled;
    // on error the button becomes available again for a retry.
    yield put(setDelayReRoute(false))
  }
}

// cluster
function* fetchClustersSaga(): Generator<any, void, any> {
  try {

    // Call API
    const clusters: TripClusterModel[] = yield call(TripApi.getTripClusters)

    // Update state
    yield put(setClusters(clusters))
  } catch (error: any) {
    console.error('Trip fetch error:', error)
    const errorMessage = error?.response?.data?.message || 'Failed to fetch clusters'
    yield put(setError(errorMessage))
    toast.error(errorMessage, ToastDefaultConfig())
  } finally {
    yield put(setLoading(false))
  }
}

// export CSV
function* exportTripCSVSaga(): Generator<any, void, any> {
  try {
    const filters = yield select(selectTripFilters)

    const cmd: TripCmd = {
      serviceDate: filters.serviceDate
        ? moment(filters.serviceDate).toISOString()
        : new Date().toISOString(),
      status: filters.status || '',
      clusterId: filters.clusterId || undefined,
      search: filters.search || '',
      clientTimeZone: filters.clientTimeZone ?? new Date().getTimezoneOffset() * -1,
      language: filters.language || 'en',
    }

    const { blob, fileName }: TripExportResult = yield call(TripApi.exportTripCSV, cmd)

    const downloadFileName =
      fileName ||
      `Trips_Export_${moment().locale('en').format('YYYYMMDD_HHmm')}.csv`

    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = downloadFileName

    document.body.appendChild(link)
    link.click()
    link.remove()

    window.URL.revokeObjectURL(url)

    toast.success('Export completed', ToastDefaultConfig())

    yield put(requestExportTripCSVSuccess())
  } catch (error: any) {
    const errorMessage = error?.message || 'Export failed'
    toast.error(errorMessage, ToastDefaultConfig())
    yield put(requestExportTripCSVFail())
  }
}

// unassigned tickets
function* fetchUnassignedTicketsSaga(): Generator<any, void, any> {
  try {

    // Call API
    const unassignedTickets: GetTripTicketQry[] = yield call(TripApi.getUnassignedTickets, {
      clientTimeZone: clientTimeZoneOffset,
    })
    console.log("unassignedTickets", unassignedTickets);
    
    // Update state
    yield put(setUnassignedTickets(unassignedTickets))
  } catch (error: any) {
    console.error('Trip fetch error:', error)
    const errorMessage = error?.response?.data?.message || 'Failed to fetch unassigned tickets'
    yield put(setError(errorMessage))
    toast.error(errorMessage, ToastDefaultConfig())
  } finally {
    yield put(setLoading(false))
  }
}

// Poll: is manual routing enabled + how many tickets are unassigned.
function* checkEnabledManualRoutingSaga(): Generator<any, void, any> {
  try {
    const res: Response<CheckEnabledManualRoutingQry> = yield call(
      TripApi.checkEnabledManualRouting,
      clientTimeZoneOffset
    )

    if (res?.status !== HTTP_RESPONSE_STATUS.SUCCESS || !res.data) {
      return
    }

    yield put(
      setManualRoutingStatus({
        isEnabled: !!res.data.isEnabled,
        unassignedTicket: res.data.unassignedTicket ?? 0,
      })
    )
  } catch (error: any) {
    // Silent: this runs on a timer in the background, no toast on failure.
    console.error('checkEnabledManualRoutingSaga - error:', error)
  }
}

// Fetch the current cooldown state (called when the modal opens) and mirror
// `cooldownUntil` into redux so the countdown reflects the backend even after
// a reload or when SignalR missed the `buttonCooldown` push.
function* fetchManualRoutingCooldownSaga(): Generator<any, void, any> {
  try {
    const res: Response<ManualRoutingCooldownQry> = yield call(TripApi.getManualRoutingCooldown)

    if (res?.status !== HTTP_RESPONSE_STATUS.SUCCESS || !res.data) {
      return
    }

    const { isOnCooldown, cooldownUntil } = res.data
    const untilMs = isOnCooldown && cooldownUntil ? new Date(cooldownUntil).getTime() : NaN

    // Set the cooldown end when active; otherwise clear any stale value.
    yield put(
      setManualRouteCooldownUntil(
        Number.isFinite(untilMs) && untilMs > Date.now() ? untilMs : null
      )
    )
  } catch (error: any) {
    console.error('fetchManualRoutingCooldownSaga - error:', error)
  }
}

// manual routing
function* manualRouteTripSaga(): Generator<any, void, any> {
  try {
    // Disable the button immediately while the request is in flight.
    yield put(setManualRouting(true))

    // Send all unassigned tickets to the routing engine (no request body).
    const res: Response<number> = yield call(TripApi.manualRouteTrip)

    // The API returns an envelope; status !== SUCCESS is a logical failure.
    if (res?.status !== HTTP_RESPONSE_STATUS.SUCCESS) {
      toast.error(res?.message || 'Failed to send tickets to routing engine', ToastDefaultConfig())
      return
    }

    // `data` is the number of tickets the engine actually routed
    // (e.g. "inserted N TripTicket(s)").
    const routedCount = typeof res?.data === 'number' ? res.data : 0

    // Refresh the unassigned list so the table + INFO banner reflect what's
    // left. Tickets that weren't routed stay in the list either way.
    yield call(fetchUnassignedTicketsSaga)

    if (routedCount <= 0) {
      // Nothing was routed: keep the modal list and the Send button enabled
      // (no cooldown is stamped here) so the operator can retry right away.
      yield put(setManualRouteSuccessCount(0))
      toast.warning(
        i18n.t('TRIP_PAGE.MANUAL_ROUTING_MODAL.NO_TICKETS_SENT'),
        ToastDefaultConfig()
      )
      return
    }

    // At least one ticket routed - expose the count for the SUCCESS banner.
    // The cooldown itself is driven by the backend `until` value delivered
    // over SignalR / the cooldown API, not stamped here.
    yield put(setManualRouteSuccessCount(routedCount))
  } catch (error: any) {
    console.error('manualRouteTripSaga - error:', error)
    const errorMessage =
      error?.response?.data?.message || 'Failed to send tickets to routing engine'
    toast.error(errorMessage, ToastDefaultConfig())
  } finally {
    yield put(setManualRouting(false))
  }
}

// Watcher Saga
export function* tripSaga() {
  yield takeLatest(FETCH_TRIPS, fetchTripsSaga)
  yield takeEvery(FETCH_TRIP_TICKETS, fetchTripTicketsSaga)
  yield takeLatest(REROUTE, reRouteStrip)
  yield takeLatest(FETCH_CLUSTERS, fetchClustersSaga)
  yield takeLatest(EXPORT_TRIP_CSV, exportTripCSVSaga)
  yield takeLatest(FETCH_UNASSIGNED_TICKETS, fetchUnassignedTicketsSaga)
  yield takeLatest(MANUAL_ROUTE_TRIP, manualRouteTripSaga)
  yield takeLatest(CHECK_ENABLED_MANUAL_ROUTING, checkEnabledManualRoutingSaga)
  yield takeLatest(GET_MANUAL_ROUTING_COOLDOWN, fetchManualRoutingCooldownSaga)
}
